import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BirdzDialog,
  BirdzNotif,
  BirdzTitle,
  useDialog,
  useNotif
} from '@applications-terrains/birdz-react-library';
import { Grid, IconButton } from '@mui/material';
import { Edit } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import BTable from '../../../Tools/Table/BTable';
import PagePaper from '../../../Tools/Pages/PagePaper';
import { useFetchDataRefsContext } from '../../../../contexts/FetchDataRefsContext';
import { equipmentConfig } from '../params.config';
import { generateListFields } from '../utils';
import ModalWithLoader, {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import useRefresh from '../../../../hooks/useRefresh';
import FieldsForm from '../FieldsForm';
import { AddCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const EquipmentFamilies = () => {
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();
  const { notif, notifOptions } = useNotif();
  const { dataRefs } = useFetchDataRefsContext();
  const config = equipmentConfig['families' as keyof typeof equipmentConfig];
  const listFields = generateListFields(config);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(null);
  const [action, setAction] = useState<ModalWithLoaderAction>(null);
  const [id, setId] = useState<string | null>(null);
  const { refresh, triggerRefresh } = useRefresh();

  const { t } = useTranslation();
  const basicActions = [
    {
      name: 'edit',
      label: t('equipmentFamilies.editFamily'),
      render: (item: any) => {
        return (
          <IconButton
            onClick={() => {
              setAction('edit');
              setId(item.id);
            }}
          >
            <Edit fontSize="small" color="action" />
          </IconButton>
        );
      }
    },
    {
      name: 'delete',
      label: t('equipmentFamilies.deleteFamily'),
      render: (param: any) => {
        return (
          <IconButton
            onClick={() => {
              confirmDialog({
                title: t('equipmentFamilies.deleteFamilyTitle'),
                content: t('equipmentFamilies.deleteFamilyContent'),
                onValidate: () => {
                  axios.delete(`/api/boi/exocet/families/${param.id}/`).then(
                    () => {
                      notif({
                        content: t('equipmentFamilies.deleteFamilySuccess'),
                        type: 'success'
                      });
                      triggerRefresh();
                      setAction(null);
                      setId(null);
                    },
                    () => {
                      notif({
                        content: t('equipmentFamilies.deleteError'),
                        type: 'error'
                      });
                    }
                  );
                  closeDialog();
                },
                onCancel: () => {
                  closeDialog();
                }
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      }
    }
  ];

  const handleCloseModalWithLoader = () => {
    setAction(null);
    setRequestStatus(null);
    setId(null);
  };

  useEffect(() => {
    if (requestStatus === 'success') {
      triggerRefresh();
      setId(null);
    }
  }, [requestStatus, triggerRefresh]);

  return (
    <PagePaper
      loader={!(`equipment-families` in dataRefs)}
      alwaysShowContent
      title={
        <Grid justifyContent="space-between" alignItems={'center'} container>
          <Grid item>
            <BirdzTitle className="m-0">{t('equipmentFamilies.title')}</BirdzTitle>
          </Grid>
        </Grid>
      }
    >
      <BTable
        model="equipment-families"
        endpoint={config?.endpoint}
        fields={listFields}
        actions={basicActions}
        refresh={refresh}
        headerActions={[
          {
            name: t('equipmentFamilies.addFamily'),
            element: (
              <IconButton onClick={() => setAction('add')}>
                <AddCircle fontSize="small" color="action" />
              </IconButton>
            )
          }
        ]}
      />
      <ModalWithLoader
        openModal={action === 'add' || action === 'edit'}
        onClose={() => handleCloseModalWithLoader()}
        onSuccess={() => handleCloseModalWithLoader()}
        action={
          action === 'delete'
            ? t('equipmentFamilies.deleteFamilyAction')
            : action === 'edit'
              ? t('equipmentFamilies.editFamilyAction')
              : t('equipmentFamilies.createFamilyAction')
        }
        status={requestStatus}
        setStatus={setRequestStatus}
      >
        <FieldsForm
          endpoint={config?.endpoint}
          setRequestStatus={setRequestStatus}
          paramConfig={config}
          action={action}
          setAction={setAction}
          id={id}
        />
      </ModalWithLoader>
      <BirdzDialog options={dialogOptions} />
      <BirdzNotif options={notifOptions} />
    </PagePaper>
  );
};

export default EquipmentFamilies;
